import { useState } from 'react';
import { Alert, App, Button, Spin } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router';
import {
  ARRANGEMENT_ANY_DATE_WINDOW,
  ARRANGEMENT_TYPES,
  PAGINATION,
  TEMPLATE_RUN_MAX_ITEMS,
  type ArrangementListItemDto,
  type CreateTemplateRunDto,
  type Paginated,
  type PublishPreviewDto,
  type TemplateRunDto,
} from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { api, type ApiError } from '@/lib/api';
import { templateSourceLabel } from '@/lib/labels';
import { queryKeys } from '@/lib/queryKeys';
import { RunProgress } from '../components/RunProgress';
import { DECISIONS_COPY, PUBLISH_COPY, TEMPLATES_COPY } from '../copy';
import { typesForEvent, useTemplateSet } from '../templateQueries';
import { PREVIEW_CONCURRENCY, mapWithConcurrency } from './concurrency';

type Checked = { draft: ArrangementListItemDto; preview: PublishPreviewDto };

/**
 * [DP2026] What the page holds for one booklet: the drafts it made, checked; and the typed drafts made from
 * no booklet at all (a corrigendum or a copy of a typed order carries the type and never the template link).
 */
type Publishable = { checked: Checked[]; unlinked: ArrangementListItemDto[] };

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B §4f] BULK PUBLISH — `/templates/:setKey/publish?type=` (exit criterion 12).
 *
 *  ⚠️ EVERY DRAFT OF THE TYPE, NOT A PAGE OF THEM. The list is read with `type`, `status=DRAFT`,
 *  `show=current` AND THE ANY-DATE WINDOW — without the window every draft more than seven days out is
 *  silently missing, and a run would publish a subset while this page reported completion — paging to
 *  exhaustion at the largest page size.
 *
 *  ⚠️ EACH DRAFT IS CHECKED WITH THE EXISTING `publish-preview`, FOUR AT A TIME, and grouped: ready; held
 *  back because an in-charge is not named (the typed blocker, decision 2); any other blocker, listed.
 *  Approval sends the READY ids, and the server re-derives the eligible set itself rather than trusting
 *  them. Each order is then published by the ordinary publish, in its own transaction, as the run
 *  advances — and the report is read back from the orders.
 *
 *  ⚠️ [amended DP2026] ONLY THIS BOOKLET'S DRAFTS ARE CHECKED AND OFFERED. Two booklets of one event now make
 *  orders of the same type, and a run accepts only the drafts its own set made (`template_source.set_id`) —
 *  so the other booklet's drafts were listed here and then dropped by the server without a word. They are
 *  left to their own booklet's page. A typed draft made from NO booklet is listed apart, unchecked, with a
 *  link to publish it from its own page, so it is never silently missing. The query key carries the set.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
const TemplatePublishPage = (): React.ReactElement => {
  const { setKey = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const set = useTemplateSet(setKey);
  const runId = searchParams.get('run');
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const types = typesForEvent(set.data?.event);
  const requested = searchParams.get('type');
  const type = types.find((key) => key === requested) ?? types[0];

  const checked = useQuery({
    queryKey: queryKeys.templates.publishable(setKey, type ?? ''),
    queryFn: async ({ signal }): Promise<Publishable> => {
      const drafts: ArrangementListItemDto[] = [];
      let page = 1;
      let pages = 1;
      do {
        const answer = await api.get<Paginated<ArrangementListItemDto>>('/arrangements', {
          query: {
            type,
            status: 'DRAFT',
            show: 'current',
            ...ARRANGEMENT_ANY_DATE_WINDOW,
            page,
            limit: PAGINATION.MAX_LIMIT,
            sortBy: 'paDate',
            sortOrder: 'asc',
          },
          signal,
        });
        drafts.push(...answer.items);
        pages = answer.pages;
        page += 1;
      } while (page <= pages);

      const mine = drafts.filter((draft) => draft.templateSource?.setKey === setKey);
      const unlinked = drafts.filter((draft) => draft.templateSource === undefined);
      setProgress({ done: 0, total: mine.length });
      const previewed = await mapWithConcurrency(
        mine,
        PREVIEW_CONCURRENCY,
        async (draft) => ({
          draft,
          preview: await api.get<PublishPreviewDto>(`/arrangements/${draft.id}/publish-preview`, { signal }),
        }),
        (done) => {
          setProgress({ done, total: mine.length });
        },
      );
      return { checked: previewed, unlinked };
    },
    enabled: runId === null && type !== undefined,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const approve = useMutation({
    mutationFn: (body: CreateTemplateRunDto) => api.post<TemplateRunDto>(`/templates/sets/${setKey}/runs`, body),
    onSuccess: (run) => {
      queryClient.setQueryData(queryKeys.templates.run(run.id), run);
      void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.all });
      setSearchParams(
        (current) => {
          current.set('run', run.id);
          return current;
        },
        { replace: true },
      );
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const label = type === undefined ? '' : ARRANGEMENT_TYPES[type].label;
  const header = (
    <PageHeader
      icon={Send}
      parent={{ label: DECISIONS_COPY.parent, to: `/templates/${setKey}` }}
      title={PUBLISH_COPY.title(label)}
    />
  );

  if (runId !== null) {
    return (
      <div className="flex flex-col gap-4" data-testid="template-publish">
        {header}
        <RunProgress runId={runId} />
      </div>
    );
  }

  if (set.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spin />
      </div>
    );
  }
  if (type === undefined) {
    return (
      <div className="flex flex-col gap-4" data-testid="template-publish">
        {header}
        <EmptyState title={TEMPLATES_COPY.noTypeForEvent} />
      </div>
    );
  }

  if (checked.isPending || checked.isFetching) {
    return (
      <div className="flex flex-col gap-4" data-testid="template-publish">
        {header}
        <div
          className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground"
          data-testid="publish-checking"
        >
          <Spin />
          {progress === null ? PUBLISH_COPY.loading : PUBLISH_COPY.checking(progress.done, progress.total)}
        </div>
      </div>
    );
  }
  if (!checked.data) {
    return (
      <div className="flex flex-col gap-4" data-testid="template-publish">
        {header}
        <Alert type="error" showIcon message={checked.error?.message ?? PUBLISH_COPY.loading} />
      </div>
    );
  }

  const { checked: rows, unlinked } = checked.data;
  const ready = rows.filter((row) => row.preview.blockers.length === 0);
  const held = rows.filter((row) => row.preview.blockers.length > 0 && row.draft.inChargeUnnamed === true);
  const other = rows.filter((row) => row.preview.blockers.length > 0 && row.draft.inChargeUnnamed !== true);
  const toPublish = ready.slice(0, TEMPLATE_RUN_MAX_ITEMS).map((row) => row.draft.id);

  const group = (title: string, rows: readonly Checked[], testId: string, withBlockers: boolean, body?: string) => (
    <section className="flex flex-col gap-1 text-sm" data-testid={testId}>
      <h2 className="font-medium text-heading">
        {title} ({rows.length})
      </h2>
      {body ? <p className="text-muted-foreground">{body}</p> : null}
      {rows.length === 0 ? (
        <span className="text-muted-foreground">{PUBLISH_COPY.none}</span>
      ) : (
        <ul className="flex flex-col gap-1">
          {rows.map(({ draft, preview }) => (
            <li key={draft.id} className="flex flex-col" data-testid="publish-draft">
              <span className="flex flex-wrap items-baseline gap-x-2">
                <Link to={`/arrangements/${draft.id}`} className="text-link">
                  {draft.paNumber ?? draft.reference}
                </Link>
                <span>{draft.displayTitle}</span>
                {draft.templateSource ? (
                  <span className="text-xs text-muted-foreground">{templateSourceLabel(draft.templateSource)}</span>
                ) : null}
              </span>
              {withBlockers
                ? preview.blockers.map((blocker) => (
                    <span key={blocker} className="text-xs text-muted-foreground">
                      {blocker}
                    </span>
                  ))
                : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  return (
    <div className="flex flex-col gap-4" data-testid="template-publish">
      {header}
      {rows.length === 0 && unlinked.length === 0 ? (
        <EmptyState title={PUBLISH_COPY.noDrafts(label)} />
      ) : (
        <>
          {group(PUBLISH_COPY.readyTitle, ready, 'publish-ready', false)}
          {group(PUBLISH_COPY.heldTitle, held, 'publish-held', true, PUBLISH_COPY.heldBody)}
          {group(PUBLISH_COPY.blockedTitle, other, 'publish-blocked', true)}
          {unlinked.length === 0 ? null : (
            <section className="flex flex-col gap-1 text-sm" data-testid="publish-unlinked">
              <h2 className="font-medium text-heading">
                {PUBLISH_COPY.unlinkedTitle} ({unlinked.length})
              </h2>
              <p className="text-muted-foreground">{PUBLISH_COPY.unlinkedBody}</p>
              <ul className="flex flex-col gap-1">
                {unlinked.map((draft) => (
                  <li
                    key={draft.id}
                    className="flex flex-wrap items-baseline gap-x-2"
                    data-testid="publish-unlinked-draft"
                  >
                    <Link to={`/arrangements/${draft.id}`} className="text-link">
                      {draft.paNumber ?? draft.reference}
                    </Link>
                    <span>{draft.displayTitle}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="primary"
          data-testid="publish-approve"
          disabled={toPublish.length === 0}
          loading={approve.isPending}
          onClick={() => {
            modal.confirm({
              title: PUBLISH_COPY.approveConfirmTitle(toPublish.length),
              content: <span className="text-sm">{PUBLISH_COPY.approveConfirmBody}</span>,
              okText: PUBLISH_COPY.approve(toPublish.length),
              onOk: () => {
                approve.mutate({ kind: 'PUBLISH', arrangementIds: toPublish });
              },
            });
          }}
        >
          {PUBLISH_COPY.approve(toPublish.length)}
        </Button>
        <Button
          onClick={() => {
            setProgress(null);
            void checked.refetch();
          }}
        >
          {PUBLISH_COPY.recheck}
        </Button>
      </div>
    </div>
  );
};

export default TemplatePublishPage;
